import ExcelJS from 'exceljs';
import dayjs from 'dayjs';
import { getGroupRegistrantSummary } from './groupRegistrantSummary';
import { formatDuration, getAssignedVideoTotal, getSessionDurationSeconds, getVideoDurationSeconds } from './sessionDuration';
import { getAttendanceStatus } from './registrantAttendance';
import { isAttendanceGroup } from './sessionAssignmentGroups';

export const XLSX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const ATTENDANCE_LABELS = { attending: 'Attending', notAttending: 'Not attending', notImported: 'Not imported' };
const HEADER_FONT = { bold: true, color: { argb: 'FFEBBC64' } };
const HEADER_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E1E1E' } };
const WARNING_FONT = { bold: true, color: { argb: 'FFB91C1C' } };

const GROUP_COLUMNS = [
    { title: 'Group', key: 'group', width: 24 },
    { title: 'Venue', key: 'venue', width: 22 },
    { title: 'Date', key: 'date', width: 18 },
    { title: 'Time (WIB)', key: 'time', width: 14 },
];

const REGISTRANT_COLUMNS = [
    { title: 'Order', key: 'order', width: 8 },
    { title: 'Performer / Ensemble', key: 'name', width: 34 },
    { title: 'Type', key: 'type', width: 14 },
    { title: 'Ensemble members', key: 'members', width: 40 },
    { title: 'Event', key: 'event', width: 13 },
    { title: 'Venue attendance', key: 'attendance', width: 16 },
    { title: 'Award', key: 'award', width: 11 },
    { title: 'Teacher', key: 'teacher', width: 26 },
    { title: 'Repertoire', key: 'repertoire', width: 42 },
    { title: 'Competition category', key: 'competitionCategory', width: 24 },
    { title: 'Age category', key: 'ageCategory', width: 28 },
    { title: 'Video duration', key: 'videoDuration', width: 14 },
    { title: 'City', key: 'city', width: 16 },
    { title: 'Email', key: 'email', width: 30 },
];

const SUMMARY_COLUMNS = [
    { title: '#', key: 'number', width: 6 },
    ...GROUP_COLUMNS,
    { title: 'Registrants', key: 'registrants', width: 12 },
    { title: 'Registrants by event', key: 'byEvent', width: 36 },
    { title: 'Total teachers', key: 'teachers', width: 14 },
    { title: 'Competition categories', key: 'byCategory', width: 52 },
    { title: 'Video total', key: 'videoTotal', width: 12 },
    { title: 'Session length', key: 'sessionLength', width: 14 },
    { title: 'Over by', key: 'overBy', width: 10 },
    { title: 'Unknown durations', key: 'unknownDurations', width: 18 },
];

const ORCHESTRA_COLUMNS = [
    { title: 'Attendance group', key: 'group', width: 24 },
    { title: 'Venue', key: 'venue', width: 22 },
    { title: 'Date', key: 'date', width: 18 },
    { title: 'Planned orchestra session', key: 'plannedSession', width: 26 },
    { title: 'Performer / Registrant', key: 'name', width: 34 },
    { title: 'Type', key: 'type', width: 14 },
    { title: 'Ensemble members', key: 'members', width: 40 },
    { title: 'Event', key: 'event', width: 13 },
    { title: 'Venue attendance', key: 'attendance', width: 16 },
    { title: 'Teacher', key: 'teacher', width: 26 },
    { title: 'Performance group', key: 'performanceGroup', width: 24 },
];

export const formatGroupDate = date => (/^\d{4}-\d{2}-\d{2}$/.test(date || '') && dayjs(date).isValid()
    ? dayjs(date).format('ddd, D MMM YYYY') : date || 'Date not set');

const getGroupName = session => session.label || session.name || 'Unnamed group';

// Same lookup as the Orchestra Attendance table's planned-session label.
const getPlannedSessionLabel = (group, orchestraSessions) => {
    if (!group.orchestraSessionId) return 'No planned session';
    const session = orchestraSessions.find(item => item.id === group.orchestraSessionId
        && item.venue === group.venue && item.date === group.date);
    return session ? `Planned: ${session.time}` : 'Planned session unavailable';
};

// Group totals use complete membership, matching the board header and summary table.
const describeGroup = (session, registrants) => {
    const summary = getGroupRegistrantSummary(registrants);
    const totalSeconds = getAssignedVideoTotal(registrants);
    const sessionSeconds = getSessionDurationSeconds(session.time);
    return {
        group: getGroupName(session),
        venue: session.venueLabel || session.venue || '',
        date: formatGroupDate(session.date),
        time: session.time || 'Time pending',
        registrants: summary.total,
        byEvent: summary.events.map(({ eventId, count }) => `${eventId}: ${count}`).join(' · '),
        teachersByEvent: summary.events.map(({ eventId, teacherCount }) => `${eventId}: ${teacherCount}`).join(' · '),
        teachers: summary.totalTeachers,
        categories: summary.categories.map(category => `${category.label} — ${summary.events
            .map(({ eventId }) => `${eventId}: ${category.events[eventId] || 0}`).join(' · ')} · Total: ${category.total}`),
        videoTotal: formatDuration(totalSeconds),
        sessionLength: sessionSeconds === null ? 'Unknown' : formatDuration(sessionSeconds),
        overBy: sessionSeconds !== null && totalSeconds > sessionSeconds ? formatDuration(totalSeconds - sessionSeconds) : '',
        unknownDurations: registrants.filter(registrant => getVideoDurationSeconds(registrant) === null).length,
    };
};

const describeRegistrantRow = (registrant, index, describeRegistrant) => ({
    order: index + 1,
    ...describeRegistrant(registrant),
    attendance: ATTENDANCE_LABELS[getAttendanceStatus(registrant)],
    videoDuration: formatDuration(getVideoDurationSeconds(registrant)),
});

const addHeaderRow = (worksheet, columns) => {
    const row = worksheet.addRow(columns.map(column => column.title));
    row.eachCell(cell => {
        cell.font = HEADER_FONT;
        cell.fill = HEADER_FILL;
        cell.alignment = { vertical: 'middle', wrapText: true };
    });
    return row;
};

const addDataRow = (worksheet, columns, values) => {
    const row = worksheet.addRow(columns.map(column => values[column.key] ?? ''));
    row.alignment = { vertical: 'top', wrapText: true };
    return row;
};

// Freeze only short headers so a long group info block does not fill a phone screen.
const finishTable = (worksheet, columns, headerRow, { printable = false, freezeHeader = false } = {}) => {
    columns.forEach((column, index) => { worksheet.getColumn(index + 1).width = column.width; });
    if (freezeHeader) worksheet.views = [{ state: 'frozen', ySplit: headerRow.number }];
    if (worksheet.rowCount > headerRow.number) {
        worksheet.autoFilter = {
            from: { row: headerRow.number, column: 1 },
            to: { row: worksheet.rowCount, column: columns.length },
        };
    }
    if (printable) {
        worksheet.pageSetup = {
            orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0,
            printTitlesRow: `${headerRow.number}:${headerRow.number}`,
        };
    }
};

// Excel sheet names are limited to 31 characters and exclude : \ / ? * [ ].
const getGroupSheetName = (session, index) => `${index + 1} ${getGroupName(session)}`
    .replace(/[^A-Za-z0-9 _-]/g, '-').slice(0, 31);

const addSummarySheet = (workbook, { eventId, planningState, isDirty, exportedAt, performanceRows, attendanceRows }) => {
    const worksheet = workbook.addWorksheet('Summary');
    worksheet.addRow([`Session assignments — ${eventId}`]).font = { bold: true, size: 14 };
    worksheet.addRow([planningState && planningState.status !== 'legacy'
        ? `Competition schedule: ${planningState.status} (revision ${planningState.revision})`
        : 'Competition schedule: legacy event sessions']);
    worksheet.addRow([`Exported: ${dayjs(exportedAt).format('D MMM YYYY, HH:mm')} (device time)`]);
    if (isDirty) {
        worksheet.addRow(['Warning: this file includes unsaved board changes. Save the board and export again before sharing.'])
            .font = WARNING_FONT;
    }
    worksheet.addRow([]);
    worksheet.addRow(['Performance groups']).font = { bold: true, size: 12 };
    addHeaderRow(worksheet, SUMMARY_COLUMNS);
    if (!performanceRows.length) worksheet.addRow(['No performance groups']);
    performanceRows.forEach(({ details }, index) => {
        const row = addDataRow(worksheet, SUMMARY_COLUMNS, {
            ...details,
            number: index + 1,
            byCategory: details.categories.join('\n') || 'No registrants assigned',
        });
        row.height = Math.max(1, details.categories.length) * 15;
    });
    SUMMARY_COLUMNS.forEach((column, index) => { worksheet.getColumn(index + 1).width = column.width; });

    if (!attendanceRows.length) return;
    worksheet.addRow([]);
    worksheet.addRow(['Orchestra attendance groups']).font = { bold: true, size: 12 };
    const attendanceColumns = SUMMARY_COLUMNS.slice(0, 9).map(column => (column.key === 'time'
        ? { ...column, title: 'Planned orchestra session' } : column));
    addHeaderRow(worksheet, attendanceColumns);
    attendanceRows.forEach(({ details, plannedSession }, index) => {
        const row = addDataRow(worksheet, attendanceColumns, {
            ...details,
            number: index + 1,
            time: plannedSession,
            byCategory: details.categories.join('\n') || 'No registrants assigned',
        });
        row.height = Math.max(1, details.categories.length) * 15;
    });
};

const addAllPerformancesSheet = (workbook, performanceRows, describeRegistrant) => {
    const worksheet = workbook.addWorksheet('All Performances');
    const columns = [...GROUP_COLUMNS, ...REGISTRANT_COLUMNS];
    const headerRow = addHeaderRow(worksheet, columns);
    performanceRows.forEach(({ details, registrants }) => registrants.forEach((registrant, index) => {
        addDataRow(worksheet, columns, { ...details, ...describeRegistrantRow(registrant, index, describeRegistrant) });
    }));
    finishTable(worksheet, columns, headerRow, { printable: true, freezeHeader: true });
};

const addGroupSheet = (workbook, { session, details, registrants }, index, describeRegistrant) => {
    const worksheet = workbook.addWorksheet(getGroupSheetName(session, index));
    worksheet.addRow([details.group]).font = { bold: true, size: 14 };
    worksheet.addRow([`Venue: ${details.venue} · Date: ${details.date} · Time: ${details.time === 'Time pending' ? details.time : `${details.time} WIB`}`]);
    worksheet.addRow([`Registrants: ${details.registrants} (${details.byEvent}) · Total teachers: ${details.teachers} (${details.teachersByEvent})`]);
    worksheet.addRow([`Video total: ${details.videoTotal}${details.sessionLength !== 'Unknown' ? ` / ${details.sessionLength} session` : ''}`
        + `${details.overBy ? ` · Over by ${details.overBy}` : ''}`
        + `${details.unknownDurations ? ` · ${details.unknownDurations} unknown duration${details.unknownDurations > 1 ? 's' : ''}` : ''}`]);
    worksheet.addRow(['Competition categories']).font = { bold: true };
    if (!details.categories.length) worksheet.addRow(['No registrants assigned']);
    details.categories.forEach(category => worksheet.addRow([category]));
    worksheet.addRow([]);
    const headerRow = addHeaderRow(worksheet, REGISTRANT_COLUMNS);
    if (!registrants.length) worksheet.addRow(['', 'No registrants assigned']);
    registrants.forEach((registrant, registrantIndex) => {
        addDataRow(worksheet, REGISTRANT_COLUMNS, describeRegistrantRow(registrant, registrantIndex, describeRegistrant));
    });
    finishTable(worksheet, REGISTRANT_COLUMNS, headerRow, { printable: true });
};

const addOrchestraSheet = (workbook, attendanceRows, performanceRows, describeRegistrant) => {
    const performanceGroupByRegistrant = new Map();
    performanceRows.forEach(({ details, registrants }) => registrants.forEach(registrant => {
        performanceGroupByRegistrant.set(registrant.id, details.group);
    }));
    const worksheet = workbook.addWorksheet('Orchestra Attendance');
    const headerRow = addHeaderRow(worksheet, ORCHESTRA_COLUMNS);
    attendanceRows.forEach(({ details, registrants, plannedSession }) => registrants.forEach(registrant => {
        addDataRow(worksheet, ORCHESTRA_COLUMNS, {
            ...describeRegistrant(registrant),
            group: details.group,
            venue: details.venue,
            date: details.date,
            plannedSession,
            attendance: ATTENDANCE_LABELS[getAttendanceStatus(registrant)],
            performanceGroup: performanceGroupByRegistrant.get(registrant.id) || 'No performance assigned',
        });
    }));
    finishTable(worksheet, ORCHESTRA_COLUMNS, headerRow, { printable: true, freezeHeader: true });
};

/**
 * Builds the field-team workbook from the board currently in memory. Every registrant is included
 * regardless of on-screen search or attendance filters; staff can filter in Excel instead.
 * `describeRegistrant` supplies the board's display fields (name, type, members, event, award,
 * teacher, repertoire, competitionCategory, ageCategory, city, email).
 */
export const buildSessionAssignmentWorkbook = ({
    eventId, sessions = [], assignments = {}, orchestraSessions = [], planningState = null,
    isDirty = false, exportedAt = new Date(), describeRegistrant,
}) => {
    const workbook = new ExcelJS.Workbook();
    workbook.created = exportedAt;
    const toRow = session => {
        const registrants = assignments[session.id] || [];
        return { session, registrants, details: describeGroup(session, registrants) };
    };
    const performanceRows = sessions.filter(session => !isAttendanceGroup(session)).map(toRow);
    const attendanceRows = sessions.filter(isAttendanceGroup).map(session => ({
        ...toRow(session),
        plannedSession: getPlannedSessionLabel(session, orchestraSessions),
    }));

    addSummarySheet(workbook, { eventId, planningState, isDirty, exportedAt, performanceRows, attendanceRows });
    addAllPerformancesSheet(workbook, performanceRows, describeRegistrant);
    performanceRows.forEach((row, index) => addGroupSheet(workbook, row, index, describeRegistrant));
    if (attendanceRows.length) addOrchestraSheet(workbook, attendanceRows, performanceRows, describeRegistrant);
    return workbook;
};

export const getSessionAssignmentExportFileName = (eventId, exportedAt = new Date()) =>
    `SessionAssignments_${String(eventId).replace(/[^A-Za-z0-9_-]/g, '_')}_${dayjs(exportedAt).format('YYYY-MM-DD_HHmm')}.xlsx`;
